import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import { Col, Label, FormGroup, Row, FormFeedback } from "reactstrap";
import "@/assets/css/FormValidation.css";

import { useAppSelector } from "@/services/hooks";

import {
  selectProductOptions,
  filterTemplatesByPageType,
  type ProductStatePageType,
} from "./useProductStateHost";
import {
  toCategoryOptions,
  toSubCategoryOptions,
  toPostOptions,
} from "../dropdownOptions";
import { toPageContentEntry } from "../pageContentEntry";
import { resolveContentDeclarations } from "../UpdatePage/updatePageHelpers";
import { optionsForContentVar, withLinkArray, type ContentVarLike } from "../contentVariableHelpers";
import ArrayLinks, { type ArrayLinksProps } from "./ArrayLinks";
import ContentColumn from "./ContentColumn";
import { CONTENT_OVERRIDES } from "../../variableFields/contentOverrides";
import EntitySelectField, { type EntitySelectFieldProps } from "../../variableFields/EntitySelectField";

// Composite content types render their own validation chrome (via the
// fieldProps validationError/isValidationAttempted pass-through), so the
// host must NOT also pass the dispatcher `error` slot for them — the error
// text would appear twice. `faq` and `service` are not listed: FaqField and
// ContentServiceField render no message of their own, so they take the
// dispatcher's slot (SCRUM-2189, SCRUM-2196).
const COMPOSITE_TYPES = new Set([
  "infotext",
  "bottomblockleft",
  "bottomblockright",
  "categorymenu",
  "sitemap",
]);

// Wizard multi-selects stay in CLICK order (SCRUM-643 review fix): the legacy
// uncontrolled Selects preserved selection order in the stored array; the
// controlled rewrite must not regroup it into option-list order on the next
// change. The update screens keep the default options-order derivation.
const OrderedEntityMultiField = (props: EntitySelectFieldProps) => (
  <EntitySelectField {...props} orderFromValue />
);

// Wizard override maps on top of CONTENT_OVERRIDES — module scope, like the
// update screens' UPDATE_*_OVERRIDES maps: block identities can never churn
// with renders (a fresh identity would remount the block subtree and drop
// input focus mid-typing). The one per-render dependency (the Array:links
// write path) travels via fieldProps.linksHandler, mirroring ArrayLinksEditor
// (UpdatePage/ContentArrayLinks.jsx).
type WizardArrayLinksProps = {
  settingVar: { variable: string };
  required?: boolean;
  linksHandler: (links: Parameters<ArrayLinksProps["handleLinkArray"]>[0], variable: string) => void;
};

const WizardArrayLinks = ({ settingVar, required, linksHandler }: WizardArrayLinksProps) => (
  <>
    <Label
      className="form-control-label"
      htmlFor="example-text-input"
      md={required ? "3" : "2"}
    >
      {settingVar.variable}
    </Label>
    <ArrayLinks
      variableName={settingVar.variable}
      handleLinkArray={(links) => linksHandler(links, settingVar.variable)}
      required={required}
    />
  </>
);
WizardArrayLinks.ownsChrome = true;

// The column a variable sits in is template DATA, so both columns share this
// one map (SCRUM-2189). The legacy columns rendered nothing for some types,
// and the live midnightExpressHome templates declare an Optional
// Array:product, so those absences went; an Optional `authorname`/`date`
// String gets the same user select / date input as a Required one. Only the
// props differ per column (fieldPropsFor below: Required passes `required`
// and `maxLength`).
const WIZARD_CONTENT_OVERRIDES = {
  ...CONTENT_OVERRIDES,
  "Array:links": WizardArrayLinks,
  // click-order multis (SCRUM-643 review fix — see OrderedEntityMultiField)
  "Array:product": OrderedEntityMultiField,
  "Array:category": OrderedEntityMultiField,
  "Array:subcategory": OrderedEntityMultiField,
  "Array:posts": OrderedEntityMultiField,
};

/** One template-resolved content entry, as `computeContentErrors` reads it. */
type DeclaredContentEntry = {
  variable: string;
  value?: unknown;
  variabletype?: string;
  type?: string;
  complextype?: string;
};

/** A Link value counts as filled only when both its name and its URL are. */
function isCompleteLink(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const { name, link } = value as { name?: unknown; link?: unknown };
  return Boolean(name) && Boolean(link);
}

/**
 * Pure validation core for the wizard's content step (exported for unit
 * tests — templateSelector.validation.test.tsx). Returns an errors object
 * keyed by variable name, plus a `template` key when no template is selected.
 * The rules are the pinned legacy set: a REQUIRED variable fails on a
 * blank/whitespace string, a NaN number, an empty array, a null object, a
 * Link missing either half, and Array:text rows that are empty or
 * whitespace-only. A required variable with no value at all fails too
 * (SCRUM-1387): a template variable saved without a `value` key produces an
 * entry without one, so it arrives here as `undefined` — it used to match no
 * branch and pass silently.
 * ContentOptional variables are never validated.
 *
 * Takes DECLARED content: it switches on `variabletype`, `type` and
 * `complextype`, which since SCRUM-1514 the wizard's save state does not carry.
 * The caller passes the template-resolved copy.
 */
export function computeContentErrors(content: DeclaredContentEntry[], hasTemplate: boolean) {
  const errors: Record<string, string> = {};
  if (!hasTemplate) {
    errors.template = "Template selection is required";
  }
  content
    .filter((ctn) => ctn.variabletype === "ContentRequired")
    .forEach((contentVar) => {
      if (
        contentVar.value === undefined ||
        (typeof contentVar.value === "string" && !contentVar.value.trim()) ||
        (typeof contentVar.value === "number" && isNaN(contentVar.value)) ||
        (Array.isArray(contentVar.value) && contentVar.value.length === 0) ||
        (typeof contentVar.value === "object" && !contentVar.value) ||
        (contentVar.type === "Link" && !isCompleteLink(contentVar.value))
      ) {
        errors[contentVar.variable] = `${contentVar.variable} is required`;
      }
      if (
        contentVar.type === "Array" &&
        contentVar.complextype === "text" &&
        (!Array.isArray(contentVar.value) ||
          contentVar.value.length === 0 ||
          contentVar.value.some((val) => typeof val !== "string" || !val.trim()))
      ) {
        errors[contentVar.variable] = `${contentVar.variable} is required`;
      }
    });
  return errors;
}

type TemplateDocument = any; // any: one entry of templates_by_design_site, typed any[] in templateSlice.ts:24

/** No page type picked yet. */
const NO_PAGE_TYPE: ProductStatePageType = {};

/** One option of the template dropdown. */
type TemplateOption = { label: string; value: TemplateDocument };

/**
 * The template step of the create-page wizard. It holds the picked template and
 * the content the editor fills in. It loads nothing itself: PageFormContainer
 * fetches the templates and option lists, and resets this step by changing its
 * `key`.
 */
export type IndexTemplateSelectorProps = {
  form_type: string;
  /**
   * The page-type step's pick (PageFormContainer's state, SCRUM-2206): narrows
   * the product options and the template list. Absent or null means no pick.
   */
  pageTypeSelection?: ProductStatePageType | null;
  isValidationAttempted?: boolean;
  /** The picked template document, or `{}` when the pick is cleared. */
  onTemplateChange?: (template: TemplateDocument) => void;
  /** The wizard's content entries: each variable and its value. */
  onContentChange?: (content: any[]) => void; // any: content entries in the shape the page save sends; no client type for page content yet
  onFormValidation?: (isValid: boolean) => void;
  children?: React.ReactNode;
};

function IndexTemplateSelector(props: IndexTemplateSelectorProps) {
  const products_redux = useAppSelector((state) => state.productproperties.productproperties);
  const posts_redux = useAppSelector((state) => state.blog.posts);
  const categories_redux = useAppSelector((state) => state.categories.categories);
  const users_redux = useAppSelector((state) => state.users.users);
  const TemplatesByDesign = useAppSelector((state) => state.templates.templates_by_design_site);
  const pageTypeSelection = props.pageTypeSelection ?? NO_PAGE_TYPE;
  const pages_redux = useAppSelector((state) => state.pages.pages);

  const [selectedTemplate, setSelectedTemplate] = useState<TemplateDocument>({});
  const [Content, setContent] = useState<any[]>([]); // any: page content entries, no client type for page content yet; passed on through onContentChange

  // `Content` is what gets SAVED: each variable and its value. `resolvedContent`
  // is the same list with the template's declaration (variabletype, type,
  // complextype) derived back onto it, for rendering and validation only
  // (SCRUM-1514). The two columns split on `variabletype`, so without this the
  // create screen would show one empty column.
  const resolvedContent = useMemo(
    () => resolveContentDeclarations(Content, selectedTemplate),
    [Content, selectedTemplate]
  );

  // Validation runs on the resolved list: the rules switch on fields the saved
  // entries no longer carry, so passing `Content` would accept an empty page.
  const validationErrors = computeContentErrors(resolvedContent, Boolean(selectedTemplate._id));

  // --- syncing Content outward ----------------------------------------------

  // The parent (PageFormContainer's page.page_content) is the only owner outside
  // this component. Content is not mirrored into redux: the one screen that read
  // that copy was the header/footer editor, which adopted a stale wizard draft
  // over the page it had loaded (SCRUM-2190).
  const isValid = Object.keys(validationErrors).length === 0;
  const { onFormValidation, onContentChange } = props;
  useEffect(() => {
    onFormValidation?.(isValid);
  }, [onFormValidation, isValid]);

  useEffect(() => {
    onContentChange?.(Content);
  }, [onContentChange, Content]);

  // --- dropdown options -------------------------------------------------------

  const localProducts = useMemo(
    () => selectProductOptions(products_redux, pageTypeSelection),
    [products_redux, pageTypeSelection]
  );
  const localCategories = useMemo(() => toCategoryOptions(categories_redux), [categories_redux]);
  const localSubCategories = useMemo(() => toSubCategoryOptions(categories_redux), [categories_redux]);
  // ContentStringField's authorname select lists the content editors.
  const localUsers = useMemo(
    () =>
      (users_redux ?? [])
        .filter((user) => user.role === "CONTENTEDITOR")
        .map((user) => ({ label: user.name, value: user.name })),
    [users_redux]
  );
  const localPosts = useMemo(() => toPostOptions(posts_redux), [posts_redux]);

  // This screen differs from the update screens: a String reads the users.
  const optionSources = {
    products: localProducts,
    categories: localCategories,
    subcategories: localSubCategories,
    posts: localPosts,
  };
  const optionExtras = { String: localUsers };
  const contentOptionsFor = (contentVar: ContentVarLike) =>
    optionsForContentVar(contentVar, optionSources, optionExtras);

  // --- event handlers ---------------------------------------------------------

  const handleTemplateSelect = (option: TemplateOption | null) => {
    const template = option ? option.value : {};
    setSelectedTemplate(template);
    props.onTemplateChange?.(template);

    if (!template.design) {
      setContent([]);
      return;
    }
    // Entries carry the variable and its value only; the declaration is derived
    // back by `resolvedContent` (SCRUM-1514).
    const { ContentRequired, ContentOptional } = template.content_variables;
    setContent(
      [...ContentRequired, ...ContentOptional].map((v) => toPageContentEntry(v, v.value))
    );
  };

  const handleContentChange = (name: string, value: unknown) => {
    let nextValue = value;
    // Product multi-selects keep sponsored products first.
    if (Array.isArray(nextValue) && nextValue[0]?.product) {
      nextValue = [...nextValue].sort((x, y) =>
        x.product.sponsored === y.product.sponsored ? 0 : x.product.sponsored ? -1 : 1
      );
    }
    setContent((prev) =>
      prev.map((v) => (v.variable === name ? { ...v, value: nextValue } : v))
    );
  };

  const handleLinkArrayChange: WizardArrayLinksProps["linksHandler"] = (links, variableName) => {
    setContent((prev) => withLinkArray(prev, links, variableName));
  };

  // --- render -----------------------------------------------------------------

  if (!TemplatesByDesign.length) return null;

  const selectTemplates = filterTemplatesByPageType(
    TemplatesByDesign.map((temp) => ({ label: temp.name, value: temp })),
    pageTypeSelection.type
  );

  // Composite types render their own validation message via fieldProps;
  // passing it here too would show it twice.
  const errorFor = (contentVar: { variable: string; type?: string }) =>
    props.isValidationAttempted && !COMPOSITE_TYPES.has(contentVar.type ?? "")
      ? validationErrors[contentVar.variable]
      : undefined;

  return (
    <>
      <FormGroup className="col-md-6">
        <Row>
          <Label className="form-control-label" md="2">Select Design Template</Label>
          <Col md="10">
            <Select
              name="template"
              options={selectTemplates}
              onChange={handleTemplateSelect}
              isClearable
              isSearchable
              required
              className={validationErrors.template ? "is-invalid" : ""}
            />
            {props.isValidationAttempted && validationErrors.template && (
              <FormFeedback>{validationErrors.template}</FormFeedback>
            )}
          </Col>
        </Row>
      </FormGroup>

      {props.children}

      {selectedTemplate._id && (
        <>
          <Col md="12">
            <center>
              <h3
                className="mb-0 text-center"
                style={{ borderBottom: "1px solid #d0d2d6", lineHeight: "0.1em", margin: "10px 0 20px" }}
              >
                <span style={{ background: "#fff", padding: "0 10px" }}>
                  Template Variables Configuration
                </span>
              </h3>
            </center>
          </Col>

          <Row>
            <ContentColumn
              className="col-md-7 mt-5"
              items={resolvedContent.filter((cv) => cv.variabletype === "ContentRequired")}
              overrides={WIZARD_CONTENT_OVERRIDES}
              optionsFor={contentOptionsFor}
              onChange={handleContentChange}
              fieldPropsFor={(cv) => ({
                variant: "create",
                pages: pages_redux,
                categories: categories_redux,
                required: true,
                // the template author's character cap; Required column only
                maxLength: cv?.char_number,
                linksHandler: handleLinkArrayChange,
                validationError: validationErrors[cv.variable],
                isValidationAttempted: props.isValidationAttempted,
              })}
              errorFor={errorFor}
            />

            <ContentColumn
              className="col-md-5 mt-5"
              items={resolvedContent.filter((cv) => cv.variabletype === "ContentOptional")}
              overrides={WIZARD_CONTENT_OVERRIDES}
              optionsFor={contentOptionsFor}
              onChange={handleContentChange}
              fieldPropsFor={(cv) => ({
                variant: "create",
                pages: pages_redux,
                categories: categories_redux,
                linksHandler: handleLinkArrayChange,
                validationError: validationErrors[cv.variable],
                isValidationAttempted: props.isValidationAttempted,
              })}
              errorFor={errorFor}
            />
          </Row>
        </>
      )}
    </>
  );
}

export default IndexTemplateSelector;
